import React, { useMemo } from 'react';

import {
    AjaxListingContext,
    AjaxListingNextButton
} from '$partials/base/index.js';

import {
    EventCard,
    EventFilters
} from '$partials/ui/index.js';

import {
    useBooleanFilter,
    useMultiSelectFilter
} from '$hooks/filters.js';

import { ModuleDispatcher } from '$partials/modules/index.js';
import { PageShell } from '$partials/global/index.js';
import { useAjaxListing } from '$hooks/ajax-listing.js';

/* ========================================================================= */
/* Component(s) */
/* ========================================================================= */

/**
 * @type {import('$types/components/pages.d.ts').EventsListingPage}
 */
const EventsListingPage = (props) => {
    const page = props.data?.page;
    const timezone = props.timezone;

    const typeOptions = useMemo(() => (page?.filters?.eventTypes?.options || [])
        .filter(Boolean)
        .map((type) => ({
            label: type.title,
            value: type.slug
        })), [page?.filters?.eventTypes?.options]);

    const pastFilter = useBooleanFilter({ initialValue: page?.filters?.past || false });

    const eventTypesFilter = useMultiSelectFilter({
        initialValue: page?.filters?.eventTypes?.value || [],
        options: typeOptions
    });

    const listing = useAjaxListing({
        infinite: true,
        items: page?.items?.edges?.map((edge) => edge?.node).filter(Boolean) || [],
        page: page?.items?.pageInfo?.page || 1,
        path: `/api/v1/events${page?.url || ''}`,
        totalItems: page?.items?.total || 0,
        totalPages: page?.items?.pageInfo?.totalPages || 1,
        query: {
            past: pastFilter.value,
            eventTypes: eventTypesFilter.value
        },
    });

    const togglePast = () => {
        pastFilter.setValue(!pastFilter.value);
    };

    const toggleEventType = (slug) => {
        eventTypesFilter.toggleValue(slug);
    };

    return (
        <PageShell
            banner
            footer={props.data?.footer}
            header={props.data?.header}>
            <div className='layout-container bg-neutral-00'>
                <div className='py-16 sm:py-40'>
                    <div className='layout-grid flex-col sm:flex-row'>
                        {/* Page header and filters */}
                        <div className='w-full sm:w-4/12 lg:w-3/12 layout-grid-col'>
                            <div className='sm:sticky sm:top-110'>
                                <h1 className='type-style-heading'>
                                    {page?.title}
                                </h1>

                                {page?.introduction && (
                                    <div
                                        className='rich-text type-style-heading'
                                        dangerouslySetInnerHTML={{ __html: page.introduction }} />
                                )}

                                <div className='mt-24 sm:mt-40'>
                                    <EventFilters
                                        isPast={pastFilter.value}
                                        onToggleEventType={toggleEventType}
                                        onTogglePast={togglePast}
                                        options={typeOptions}
                                        value={eventTypesFilter.value} />
                                </div>
                            </div>
                        </div>

                        {/* The programme */}
                        <div className='w-full sm:w-8/12 lg:w-9/12 pt-38 sm:pt-0'>
                            <div className='layout-grid-col'>
                                {/* Push heading down */}
                                <div
                                    aria-hidden={true}
                                    className='hidden sm:block sm:w-4/8 lg:w-3/8 text-transparent type-style-heading select-none pointer-events-none'>
                                    {page?.title}
                                </div>

                                <p
                                    aria-live='polite'
                                    className='sr-only'
                                    role='status'>
                                    {`${listing.totalItems} ${pastFilter.value ? 'past' : 'upcoming'} events`}
                                </p>
                            </div>

                            <AjaxListingContext.Provider value={listing}>
                                {listing.items.length > 0 && (
                                    <section
                                        aria-label='Events feed'
                                        className='flex flex-wrap gap-y-32 sm:gap-y-24 mt-16 sm:mt-24'
                                        role='feed'>
                                        {listing.items.map((item, index) => (
                                            <article
                                                aria-label={item.title}
                                                aria-posinset={index + 1}
                                                aria-setsize={listing.items.length}
                                                className={index === 0 ? 'w-full lg:w-6/9 layout-grid-col' : 'w-full sm:w-4/8 lg:w-3/9 layout-grid-col'}
                                                key={item.id}>
                                                <EventCard
                                                    event={item}
                                                    isWide={index === 0}
                                                    timezone={timezone} />
                                            </article>
                                        ))}
                                    </section>
                                )}

                                {(listing?.totalPages > 1 && listing?.page !== listing?.totalPages) && (
                                    <div className='layout-grid-col mt-16'>
                                        <AjaxListingNextButton
                                            className='button-hollow'
                                            label='Load more'>
                                            Load more
                                        </AjaxListingNextButton>
                                    </div>
                                )}
                            </AjaxListingContext.Provider>
                        </div>
                    </div>
                </div>
            </div>

            {/* The event production offering */}
            {page?.content?.length > 0 && (
                <div>
                    {page.content.map((module) => (
                        <ModuleDispatcher
                            key={module.id}
                            modulePaddingClasses='py-24 sm:py-32'
                            { ...module } />
                    ))}
                </div>
            )}
        </PageShell>
    );
};

/* ========================================================================= */
/* Typing and exports */
/* ========================================================================= */

export default EventsListingPage;
